/**
 * Cliente HTTP base para comunicação com a API.
 */

import { env } from '@/lib/env'
import type { ApiResponse } from '@/types/api'

const STORAGE_INSTITUTION_KEY = 'legalprime:selectedInstitutionId'

/**
 * Obtém o ID da instituição selecionada do localStorage.
 * Usado por admins para impersonar instituições.
 */
export function getSelectedInstitutionId(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(STORAGE_INSTITUTION_KEY)
}

export const SELECTED_INSTITUTION_CLEARED_EVENT =
  'legalprime:selected-institution-cleared' as const

export const UNAUTHORIZED_EVENT = 'legalprime:unauthorized' as const

/** Remove a instituição impersonada (admin) do storage e notifica o InstitutionContext. */
export function clearSelectedInstitutionId(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(STORAGE_INSTITUTION_KEY)
  window.dispatchEvent(new CustomEvent(SELECTED_INSTITUTION_CLEARED_EVENT))
}

/**
 * Opções para requisições HTTP.
 */
interface RequestOptions extends RequestInit {
  requireAuth?: boolean
  token?: string
}

function serializeRequestBody(body: unknown): BodyInit | undefined {
  if (body === undefined || body === null) return undefined
  if (typeof FormData !== 'undefined' && body instanceof FormData) return body
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return body
  if (typeof body === 'string') return body
  if (body instanceof Blob) return body
  if (body instanceof ArrayBuffer) return body
  if (ArrayBuffer.isView(body)) return body as BodyInit
  return JSON.stringify(body)
}

/**
 * Classe de erro customizada para erros da API.
 */
export class ApiClientError extends Error {
  status: number
  errors?: Record<string, string[]>
  code?: string

  constructor(
    message: string,
    status: number,
    errors?: Record<string, string[]>,
    code?: string
  ) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.errors = errors
    this.code = code
  }
}

async function parseResponseBody<T>(response: Response): Promise<{
  data: ApiResponse<T> | null
  text: string | null
}> {
  if (response.status === 204) {
    return { data: null, text: null }
  }

  let raw: string
  try {
    raw = await response.text()
  } catch {
    return { data: null, text: null }
  }

  const trimmed = raw.replace(/^\uFEFF/, '').trim()
  if (!trimmed) {
    return { data: null, text: null }
  }

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return { data: JSON.parse(trimmed) as ApiResponse<T>, text: null }
    } catch {
      return { data: null, text: trimmed }
    }
  }

  return { data: null, text: trimmed }
}

function isCredentialChallengeEndpoint(endpoint: string): boolean {
  return /\/login(?:\/2fa)?$/.test(endpoint) || /\/register$/.test(endpoint) || /\/logout$/.test(endpoint)
}

function shouldBroadcastUnauthorized(endpoint: string): boolean {
  if (isCredentialChallengeEndpoint(endpoint)) return false
  return !/^\/api\/public(\/|$)/.test(endpoint)
}

/**
 * Faz uma requisição HTTP para a API. Sessão via cookie httpOnly (credentials include).
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const fetchOptions: RequestInit = { ...options }
  delete (fetchOptions as { requireAuth?: boolean }).requireAuth
  delete (fetchOptions as { token?: string }).token

  const baseUrl = env.apiBaseUrl.replace(/\/$/, '')
  const url = `${baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`

  const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData
  const headers: Record<string, string> = {
    Accept: 'application/json',
  }
  if (!isFormData) {
    headers['Content-Type'] = 'application/json'
  }

  if (fetchOptions.headers) {
    const extraHeaders = new Headers(fetchOptions.headers)
    extraHeaders.forEach((value, key) => {
      if (isFormData && key.toLowerCase() === 'content-type') return
      if (key.toLowerCase() === 'authorization') return
      headers[key] = value
    })
  }

  const selectedInstitutionId = getSelectedInstitutionId()
  if (selectedInstitutionId) {
    headers['X-Institution-Id'] = selectedInstitutionId
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      credentials: 'include',
      headers,
    })

    const { data, text } = await parseResponseBody<T>(response)

    if (!response.ok) {
      if (response.status === 413) {
        throw new ApiClientError(
          'Os arquivos enviados ultrapassam o limite aceito pelo servidor. Reduza o tamanho ou a quantidade de arquivos e tente novamente.',
          response.status
        )
      }

      if (response.status === 422) {
        throw new ApiClientError(
          data?.message || 'Erro de validação',
          response.status,
          data?.errors,
          data?.code
        )
      }

      if (response.status === 401) {
        if (typeof window !== 'undefined' && shouldBroadcastUnauthorized(endpoint)) {
          window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT))
        }
        throw new ApiClientError(
          data?.message || 'Não autenticado',
          response.status,
          undefined,
          data?.code
        )
      }

      throw new ApiClientError(
        data?.message || text || `Erro HTTP ${response.status}`,
        response.status,
        data?.errors,
        data?.code
      )
    }

    if (data?.success === false) {
      throw new ApiClientError(
        data.message || 'Erro na requisição',
        response.status,
        data.errors,
        data.code
      )
    }

    if (data && 'data' in data) {
      return data.data as T
    }

    return ({ message: data?.message } as unknown) as T
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw error
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new ApiClientError(
        'Erro de conexão. Verifique sua internet e tente novamente.',
        0
      )
    }

    throw new ApiClientError(
      error instanceof Error ? error.message : 'Erro desconhecido',
      0
    )
  }
}

/**
 * Métodos HTTP helpers.
 */
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: serializeRequestBody(body),
    }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: serializeRequestBody(body),
    }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: serializeRequestBody(body),
    }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
}
